import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import inventoryApi from '../../../services/inventoryApi';
import '../inventory.css';
import './ProductForm.css';

/**
 * ProductRelatedOrders Component
 * 
 * Shows all orders related to products:
 * - purchase_order_items (Many-to-One to products)
 * - sales_order_items (Many-to-One to products)
 * - return_order_items (Many-to-One to products)
 */
const ProductRelatedOrders = ({ organization = 'TestOrg' }) => {
  const { t } = useTranslation();
  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [activeOrderType, setActiveOrderType] = useState('purchase');
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [salesOrders, setSalesOrders] = useState([]);
  const [returnOrders, setReturnOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadProducts();
  }, [organization]);

  useEffect(() => {
    if (selectedProduct) {
      loadOrders();
    }
  }, [selectedProduct, activeOrderType]);

  const loadProducts = async () => {
    try {
      setLoading(true);
      const response = await inventoryApi.getProducts(organization);
      const productsData = Array.isArray(response) ? response : (response.data || []);
      setProducts(productsData);
      if (productsData.length > 0) {
        setSelectedProduct(productsData[0].id);
      }
    } catch (err) {
      setError(err.message || 'Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  const loadOrders = async () => {
    if (!selectedProduct) return;

    try {
      setLoading(true);
      setError(null);

      if (activeOrderType === 'purchase') {
        const response = await inventoryApi.getPurchaseOrders(organization);
        const orders = Array.isArray(response) ? response : (response.data || []);
        
        const results = await Promise.all(orders.map(async (order) => {
          const items = await inventoryApi.getPurchaseOrderItems(order.id);
          const itemsData = Array.isArray(items) ? items : (items.data || []);
          const productItems = itemsData.filter(item => 
            (item.product_id || item.productId) === selectedProduct
          );
          return productItems.length > 0 ? { ...order, items: productItems } : null;
        }));
        setPurchaseOrders(results.filter(Boolean));
        
      } else if (activeOrderType === 'sales') {
        const response = await inventoryApi.getSalesOrders(organization);
        const orders = Array.isArray(response) ? response : (response.data || []);
        
        const results = await Promise.all(orders.map(async (order) => {
          const items = await inventoryApi.getSalesOrderItems(order.id);
          const itemsData = Array.isArray(items) ? items : (items.data || []);
          const productItems = itemsData.filter(item => 
            (item.product_id || item.productId) === selectedProduct
          );
          return productItems.length > 0 ? { ...order, items: productItems } : null;
        }));
        setSalesOrders(results.filter(Boolean));
        
      } else if (activeOrderType === 'return') {
        const response = await inventoryApi.getReturnOrders(organization);
        const orders = Array.isArray(response) ? response : (response.data || []);
        
        const results = await Promise.all(orders.map(async (order) => {
          const items = await inventoryApi.getReturnOrderItems(order.id);
          const itemsData = Array.isArray(items) ? items : (items.data || []);
          const productItems = itemsData.filter(item => 
            (item.product_id || item.productId) === selectedProduct
          );
          return productItems.length > 0 ? { ...order, items: productItems } : null;
        }));
        setReturnOrders(results.filter(Boolean));
      }
    } catch (err) {
      setError(err.message || 'Failed to load orders');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(value || 0);
  };

  const getStatusClass = (status) => {
    const statusMap = {
      'draft': 'status-draft',
      'confirmed': 'status-confirmed',
      'in_progress': 'status-in-progress',
      'completed': 'status-completed',
      'cancelled': 'status-cancelled',
      'delivered': 'status-delivered',
      'partial': 'status-partial'
    };
    return statusMap[status?.toLowerCase()] || 'status-other';
  };

  const renderPurchaseOrders = () => (
    <div>
      {loading ? (
        <div className="pm-skeleton" aria-busy="true">
          {t('inventory.loading_purchase_orders')}
        </div>
      ) : purchaseOrders.length === 0 ? (
        <div className="pm-empty-state">
          <div className="pm-empty-state-icon" aria-hidden="true">📋</div>
          <h3>{t('inventory.no_purchase_orders')}</h3>
        </div>
      ) : (
        purchaseOrders.map(order => (
          <div key={order.id} className="pm-order-card">
            <div className="pm-card-header">
              <div className="pm-card-info">
                <h4>{t('inventory.po_prefix')}{order.po_number || order.poNumber || order.id}</h4>
                <span className={`pm-status-badge ${getStatusClass(order.status)}`}>
                  {(order.status || 'draft').toUpperCase()}
                </span>
              </div>
              <div className="pm-card-meta">
                <span>{t('inventory.date_label')} {formatDate(order.order_date || order.orderDate)}</span>
                <span>{t('inventory.supplier_label')} {order.supplier_name || order.supplierName || t('inventory.na_fallback')}</span>
              </div>
            </div>
            <div className="pm-card-content">
              <table className="pm-data-table">
                <thead>
                  <tr>
                    <th>{t('inventory.quantity_header')}</th>
                    <th>{t('inventory.unit_price_header')}</th>
                    <th>{t('inventory.total_header')}</th>
                    <th>{t('inventory.received_header')}</th>
                    <th>{t('inventory.status_header')}</th>
                  </tr>
                </thead>
                <tbody>
                  {order.items.map((item, idx) => (
                    <tr key={idx}>
                      <td>{item.quantity}</td>
                      <td>{formatCurrency(item.unit_price || item.unitPrice)}</td>
                      <td>{formatCurrency((item.quantity || 0) * (item.unit_price || item.unitPrice || 0))}</td>
                      <td>{item.quantity_received || item.quantityReceived || 0}</td>
                      <td>
                        <span className={`pm-status-badge ${(item.quantity_received || item.quantityReceived || 0) >= (item.quantity || 0) ? 'status-completed' : 'status-partial'}`}>
                          {(item.quantity_received || item.quantityReceived || 0) >= (item.quantity || 0) ? t('inventory.complete_status') : t('inventory.partial_status')}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ))
      )}
    </div>
  );

  const renderSalesOrders = () => (
    <div>
      {loading ? (
        <div className="pm-skeleton" aria-busy="true">
          {t('inventory.loading_sales_orders')}
        </div>
      ) : salesOrders.length === 0 ? (
        <div className="pm-empty-state">
          <div className="pm-empty-state-icon" aria-hidden="true">📋</div>
          <h3>{t('inventory.no_sales_orders')}</h3>
        </div>
      ) : (
        salesOrders.map(order => (
          <div key={order.id} className="pm-order-card">
            <div className="pm-card-header">
              <div className="pm-card-info">
                <h4>{t('inventory.so_prefix')}{order.order_number || order.orderNumber || order.id}</h4>
                <span className={`pm-status-badge ${getStatusClass(order.status)}`}>
                  {(order.status || 'draft').toUpperCase()}
                </span>
              </div>
              <div className="pm-card-meta">
                <span>{t('inventory.date_label')} {formatDate(order.order_date || order.orderDate)}</span>
                <span>{t('inventory.customer_label')} {order.customer_name || order.customerName || t('inventory.na_fallback')}</span>
              </div>
            </div>
            <div className="pm-card-content">
              <table className="pm-data-table">
                <thead>
                  <tr>
                    <th>{t('inventory.quantity_header')}</th>
                    <th>{t('inventory.unit_price_header')}</th>
                    <th>{t('inventory.discount_header')}</th>
                    <th>{t('inventory.total_header')}</th>
                    <th>{t('inventory.fulfilled_header')}</th>
                  </tr>
                </thead>
                <tbody>
                  {order.items.map((item, idx) => (
                    <tr key={idx}>
                      <td>{item.quantity}</td>
                      <td>{formatCurrency(item.unit_price || item.unitPrice)}</td>
                      <td>{item.discount_percent || item.discountPercent || 0}%</td>
                      <td>{formatCurrency((item.quantity || 0) * (item.unit_price || item.unitPrice || 0))}</td>
                      <td>{item.quantity_fulfilled || item.quantityFulfilled || 0}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ))
      )}
    </div>
  );

  const renderReturnOrders = () => (
    <div>
      {loading ? (
        <div className="pm-skeleton" aria-busy="true">
          {t('inventory.loading_return_orders')}
        </div>
      ) : returnOrders.length === 0 ? (
        <div className="pm-empty-state">
          <div className="pm-empty-state-icon" aria-hidden="true">📋</div>
          <h3>{t('inventory.no_return_orders')}</h3>
        </div>
      ) : (
        returnOrders.map(order => (
          <div key={order.id} className="pm-order-card">
            <div className="pm-card-header">
              <div className="pm-card-info">
                <h4>{t('inventory.rma_prefix')}{order.rma_number || order.rmaNumber || order.id}</h4>
                <span className={`pm-status-badge ${getStatusClass(order.status)}`}>
                  {(order.status || 'draft').toUpperCase()}
                </span>
              </div>
              <div className="pm-card-meta">
                <span>{t('inventory.date_label')} {formatDate(order.return_date || order.returnDate)}</span>
                <span>{t('inventory.type_label')} {(order.return_type || order.returnType || t('inventory.na_fallback')).toUpperCase()}</span>
              </div>
            </div>
            <div className="pm-card-content">
              <table className="pm-data-table">
                <thead>
                  <tr>
                    <th>{t('inventory.quantity_header')}</th>
                    <th>{t('inventory.reason_header')}</th>
                    <th>{t('inventory.condition_header')}</th>
                    <th>{t('inventory.refund_amount_header')}</th>
                    <th>{t('inventory.processed_header')}</th>
                  </tr>
                </thead>
                <tbody>
                  {order.items.map((item, idx) => (
                    <tr key={idx}>
                      <td>{item.quantity}</td>
                      <td>{item.return_reason || item.returnReason || t('inventory.na_fallback')}</td>
                      <td>{item.product_condition || item.productCondition || t('inventory.na_fallback')}</td>
                      <td>{formatCurrency(item.refund_amount || item.refundAmount)}</td>
                      <td>{item.quantity_processed || item.quantityProcessed || 0}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ))
      )}
    </div>
  );

  return (
    <div className="pm-page-card">
      <div className="pm-header">
        <h2 className="pm-header-title">{t('inventory.product_related_orders_title')}</h2>
      </div>

      {error && (
        <div className="pm-error-banner">
          <span aria-hidden="true">⚠️</span>
          <span>{error}</span>
        </div>
      )}

      <div className="pm-product-selector">
        <label>{t('inventory.select_product_label')}</label>
        <select 
          value={selectedProduct || ''} 
          onChange={(e) => setSelectedProduct(Number(e.target.value))}
        >
          <option value="">{t('inventory.select_product_option')}</option>
          {products.map(product => (
            <option key={product.id} value={product.id}>
              {product.name} ({product.sku || product.SKU || t('inventory.na_fallback')})
            </option>
          ))}
        </select>
      </div>

      <div className="pm-tabs">
        <button
          className={`pm-tab ${activeOrderType === 'purchase' ? 'active' : ''}`}
          onClick={() => setActiveOrderType('purchase')}
        >
          {t('inventory.purchase_orders_tab')}
        </button>
        <button
          className={`pm-tab ${activeOrderType === 'sales' ? 'active' : ''}`}
          onClick={() => setActiveOrderType('sales')}
        >
          {t('inventory.sales_orders_tab')}
        </button>
        <button
          className={`pm-tab ${activeOrderType === 'return' ? 'active' : ''}`}
          onClick={() => setActiveOrderType('return')}
        >
          {t('inventory.return_orders_tab')}
        </button>
      </div>

      <div>
        {activeOrderType === 'purchase' && renderPurchaseOrders()}
        {activeOrderType === 'sales' && renderSalesOrders()}
        {activeOrderType === 'return' && renderReturnOrders()}
      </div>
    </div>
  );
};

export default ProductRelatedOrders;
